/**
 * JS-1396 — the browser side of the per-device web session.
 *
 * Fabio (Jira 59140): the passkey is asked whenever the browser is reopened,
 * and a full login is required once a month. While any app tab is open it
 * heartbeats (and every successful api response counts too); a visit after
 * no heartbeat for longer than the reopen grace is a reopened browser ->
 * unlock screen. The api enforces the same rule (423) and the monthly limit
 * (401 -> the existing sign-out). No user input is tracked.
 *
 * Pure functions over localStorage, no Angular imports, so tests/ can run
 * them under plain node.
 */

export const WEB_SESSION_KEY = 'webSession';
export const REOPEN_GRACE_KEY = 'reopenGrace';
export const LAST_HEARTBEAT_KEY = 'lastHeartbeat';
export const UNLOCK_ROUTE = '/passkey-unlock';
export const HEARTBEAT_INTERVAL_MS = 45 * 1000;

/**
 * Every browser open (Fabio): the heartbeat grace alone lets a quick
 * quit-and-reopen through. So every app start that is not a same-tab reload
 * locks at once and asks the other tabs over TAB_CHANNEL_NAME; an answer
 * within TAB_PONG_WAIT_MS undoes the lock (another tab is open), silence means
 * the browser was opened -> REOPEN_LOCKED_KEY, shared by every tab until a
 * passkey unlock, a new login or a sign-out. Nothing a tab writes
 * periodically is trusted: it survives a quit.
 */
export const PAGEHIDE_KEY = 'appPagehideAt';
export const REOPEN_LOCKED_KEY = 'reopenLocked';
/**
 * Set once the api has accepted passkey/reopen for the current reopen lock
 * (2xx). Until then any tab that sees the lock reports it - the tab that
 * decided may reload or close inside its answer window. Cleared with the lock.
 */
export const REOPEN_REPORTED_KEY = 'reopenReported';
/**
 * Backoff for a failed report, shared by every tab: no tab posts
 * passkey/reopen before REOPEN_RETRY_AT_KEY (epoch ms). REOPEN_BACKOFF_KEY is
 * the last 5xx / network delay, doubled per failure from
 * REOPEN_RETRY_BASE_MS up to REOPEN_RETRY_MAX_MS; a 429 waits Retry-After
 * (fallback REOPEN_RETRY_429_MS). Cleared with the lock.
 */
export const REOPEN_RETRY_AT_KEY = 'reopenReportRetryAt';
export const REOPEN_BACKOFF_KEY = 'reopenReportBackoffMs';
export const REOPEN_RETRY_BASE_MS = 15000;
export const REOPEN_RETRY_MAX_MS = 5 * 60 * 1000;
export const REOPEN_RETRY_429_MS = 60000;
/**
 * Written by a tab while it waits for a pong (its own timestamp), removed
 * when it decides (or its page goes away). While it is younger than
 * REOPEN_DECIDING_STALE_MS no other tab reports: the asker may still get its
 * answer. Older (the asker died) or absent: the others report.
 */
export const REOPEN_DECIDING_KEY = 'reopenDeciding';
export const REOPEN_DECIDING_STALE_MS = 5000;
export const TAB_PONG_WAIT_MS = 500;
export const TAB_CHANNEL_NAME = 'js1396-app-tabs';
/** A reload's pagehide comes after its navigation start (the old page unloads when the new one commits). */
export const RELOAD_PAGEHIDE_SLACK_MS = 1000;
export const RELOAD_PAGEHIDE_WINDOW_MS = 10000;
/**
 * reopenLocked holds the time it was set. A tab that is already open and
 * unlocked ignores a flag younger than this when deciding whether to lock
 * itself, so its own tick / focus cannot lock it in the moment between the
 * asking tab setting the flag and this tab answering the ping.
 */
export const REOPEN_LOCK_ANSWER_GRACE_MS = 1000;

const DEFAULT_REOPEN_GRACE = 300;

// Screens where the lock must not fire or be returned to: the unlock screen
// itself (loop) and the sign-in/out flow, which runs before a session exists
// (login_token / enrolment_token / emailed reset link). Listed one by one:
// /passkey-add-device needs a signed-in session and enrols a passkey on it,
// so it is lockable like any app screen.
const NO_LOCK_PREFIXES = [
  UNLOCK_ROUTE, '/login', '/logout', '/passkey-verify', '/passkey-recovery', '/passkey-enrol',
  '/passkey-reset/', '/passkey-device-code'
];

export function storeWebSession(response: any, now: number = Date.now()): void {
  if (!response || !response.session_token) {
    // Non-gated login (e.g. an employee): every login owns these keys, so a
    // previous user's device session must not ride along on this one's
    // requests or lock them onto an unlock screen they cannot pass.
    localStorage.removeItem(WEB_SESSION_KEY);
    localStorage.removeItem(REOPEN_GRACE_KEY);
    localStorage.removeItem(LAST_HEARTBEAT_KEY);
    clearReopenLocked();
    return;
  }
  clearReopenLocked();
  localStorage.setItem(WEB_SESSION_KEY, response.session_token);
  localStorage.setItem(REOPEN_GRACE_KEY, String(response.reopen_grace || DEFAULT_REOPEN_GRACE));
  localStorage.setItem(LAST_HEARTBEAT_KEY, String(now));
}

/** An app tab is open and the api answered: the browser is not "closed". */
export function recordHeartbeat(now: number): void {
  try {
    localStorage.setItem(LAST_HEARTBEAT_KEY, String(now));
  } catch (e) {
    // Storage unavailable: the api still enforces the rule.
  }
}

/**
 * The api said this device's session is stale (423): make the front agree, so
 * the lock sticks and no tab's claim bounces the unlock screen. With `seenAt`
 * (the value a heartbeat wrote) it only acts if that value is still stored, so
 * a 423 racing another tab's unlock does not re-lock everyone.
 */
export function expireHeartbeat(seenAt?: string | null): void {
  try {
    if (seenAt != null && localStorage.getItem(LAST_HEARTBEAT_KEY) !== seenAt) {
      return;
    }
    localStorage.setItem(LAST_HEARTBEAT_KEY, '0');
  } catch (e) {
    // Storage unavailable: the api still enforces the rule.
  }
}

/** The grace the api reported (login / unlock); ignored unless a positive number. */
export function storeReopenGrace(seconds: any): void {
  const value = Number(seconds);
  if (isFinite(value) && value > 0) {
    localStorage.setItem(REOPEN_GRACE_KEY, String(value));
  }
}

export function isReopenExpired(now: number, lastHeartbeat: string | null, grace: string | null): boolean {
  if (!lastHeartbeat || !grace) {
    return false;
  }
  const last = Number(lastHeartbeat);
  const seconds = Number(grace);
  if (!isFinite(last) || !isFinite(seconds) || seconds <= 0) {
    return false;
  }
  return now - last > seconds * 1000;
}

export function hasDeviceSession(): boolean {
  return !!localStorage.getItem('currentUser') && !!localStorage.getItem(WEB_SESSION_KEY);
}

/** The page is going away (reload, same-tab navigation, close or quit). */
export function markPagehide(now: number): void {
  try {
    localStorage.setItem(PAGEHIDE_KEY, String(now));
  } catch (e) {
    // Storage unavailable: the next start just asks the other tabs.
  }
}

export interface NavigationInfo {
  type: string | null;
  /**
   * Navigation start, epoch ms on the Date.now() clock: Date.now() minus
   * performance.now() (plus the entry's startTime). performance.timeOrigin is
   * a separate clock that can drift from Date.now() after a sleep, and the
   * pagehide mark it is compared with is a Date.now() value.
   */
  start: number | null;
  /** The previous document in this same tab was same-origin and was unloaded by this navigation. */
  unloadedPrevious: boolean;
}

/**
 * Navigation Timing for this document; null without the API. A tab restored
 * after a quit (or a new tab) has no previous document in the tab, so
 * unloadEventStart is 0 - Chrome 154 reports a restored tab as 'back_forward'
 * with unloadEventStart 0.
 */
export function readNavigation(perf: any, now: number = Date.now()): NavigationInfo | null {
  if (!perf) {
    return null;
  }
  let entry: any = null;
  try {
    entry = typeof perf.getEntriesByType === 'function' ? perf.getEntriesByType('navigation')[0] || null : null;
  } catch (e) {
    entry = null;
  }
  const legacyTypes = ['navigate', 'reload', 'back_forward'];
  const legacyType = perf.navigation ? legacyTypes[perf.navigation.type] || null : null;
  const timing = perf.timing || null;
  let start: number | null = null;
  const elapsed = typeof perf.now === 'function' ? Number(perf.now()) : NaN;
  if (isFinite(elapsed) && elapsed >= 0) {
    const entryStart = entry && isFinite(Number(entry.startTime)) ? Number(entry.startTime) : 0;
    start = now - elapsed + entryStart;
  } else if (isFinite(perf.timeOrigin) && perf.timeOrigin > 0) {
    start = Number(perf.timeOrigin);
  } else if (timing && timing.navigationStart > 0) {
    start = Number(timing.navigationStart);
  }
  const unloadedPrevious = entry ? Number(entry.unloadEventStart) > 0 : !!(timing && timing.unloadEventStart > 0);
  return {type: (entry && entry.type) || legacyType, start, unloadedPrevious};
}

/**
 * A reload (or same-tab navigation) of a live app tab, not a browser open:
 * the tab's previous document was ours and its pagehide came at or after
 * this navigation's start. A quit's pagehide always precedes the restored
 * tab's start, and a restored tab has no previous document anyway.
 * back_forward counts under the same two conditions: Back across a document
 * boundary in one tab (after a language or agency switch, which set
 * location.href) unloads our previous document right after its start, while
 * a restored tab reports back_forward with nothing unloaded.
 */
export function isSameTabReload(nav: NavigationInfo | null): boolean {
  if (!nav || nav.start === null || !nav.unloadedPrevious) {
    return false;
  }
  if (nav.type !== 'reload' && nav.type !== 'navigate' && nav.type !== 'back_forward') {
    return false;
  }
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PAGEHIDE_KEY);
  } catch (e) {
    return false;
  }
  const at = raw ? Number(raw) : NaN;
  if (!isFinite(at)) {
    return false;
  }
  return at >= nav.start - RELOAD_PAGEHIDE_SLACK_MS && at <= nav.start + RELOAD_PAGEHIDE_WINDOW_MS;
}

/**
 * Stores the time the lock was set (see REOPEN_LOCK_ANSWER_GRACE_MS). A lock
 * that already holds keeps its "reported" marker; a new one starts unreported.
 */
export function setReopenLocked(now: number = Date.now()): void {
  try {
    if (!isReopenLocked()) {
      localStorage.removeItem(REOPEN_REPORTED_KEY);
      localStorage.removeItem(REOPEN_RETRY_AT_KEY);
      localStorage.removeItem(REOPEN_BACKOFF_KEY);
    }
    localStorage.setItem(REOPEN_LOCKED_KEY, String(now));
  } catch (e) {
    // Storage unavailable: the tab is still on the unlock screen.
  }
}

export function clearReopenLocked(): void {
  try {
    localStorage.removeItem(REOPEN_LOCKED_KEY);
    localStorage.removeItem(REOPEN_REPORTED_KEY);
    localStorage.removeItem(REOPEN_RETRY_AT_KEY);
    localStorage.removeItem(REOPEN_BACKOFF_KEY);
    localStorage.removeItem(REOPEN_DECIDING_KEY);
  } catch (e) {
    // Storage unavailable: nothing to clear.
  }
}

function readNumber(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    const value = raw === null || raw === '' ? NaN : Number(raw);
    return isFinite(value) ? value : null;
  } catch (e) {
    return null;
  }
}

/** No tab may post passkey/reopen before this (epoch ms); null: no wait. */
export function reopenRetryAt(): number | null {
  return readNumber(REOPEN_RETRY_AT_KEY);
}

/** A successful report: the backoff is over. */
function clearReopenBackoff(): void {
  try {
    localStorage.removeItem(REOPEN_RETRY_AT_KEY);
    localStorage.removeItem(REOPEN_BACKOFF_KEY);
  } catch (e) {
    // Storage unavailable.
  }
}

/**
 * A 5xx / network failure: wait 15 s, 30 s, 60 s ... capped at 5 min, shared
 * by every tab. Returns the delay used.
 */
export function backOffReopenReport(now: number): number {
  const previous = readNumber(REOPEN_BACKOFF_KEY);
  const delay = previous && previous > 0 ? Math.min(previous * 2, REOPEN_RETRY_MAX_MS) : REOPEN_RETRY_BASE_MS;
  try {
    localStorage.setItem(REOPEN_BACKOFF_KEY, String(delay));
    localStorage.setItem(REOPEN_RETRY_AT_KEY, String(now + delay));
  } catch (e) {
    // Storage unavailable: this tab still waits (its own timer).
  }
  return delay;
}

/**
 * Retry-After of a 429: delta seconds or an HTTP-date; anything else
 * (missing, junk, in the past) -> REOPEN_RETRY_429_MS. Capped at
 * REOPEN_RETRY_MAX_MS.
 */
export function retryAfterMs(value: string | null | undefined, now: number): number {
  if (value !== null && value !== undefined && String(value).trim() !== '') {
    const text = String(value).trim();
    if (/^\d+$/.test(text)) {
      return Math.min(Number(text) * 1000, REOPEN_RETRY_MAX_MS);
    }
    const date = Date.parse(text);
    if (isFinite(date) && date > now) {
      return Math.min(date - now, REOPEN_RETRY_MAX_MS);
    }
  }
  return REOPEN_RETRY_429_MS;
}

/** A 429: wait what the api asked for, shared by every tab. Returns the delay used. */
export function rateLimitReopenReport(now: number, retryAfter: string | null | undefined): number {
  const delay = retryAfterMs(retryAfter, now);
  try {
    localStorage.setItem(REOPEN_RETRY_AT_KEY, String(now + delay));
  } catch (e) {
    // Storage unavailable.
  }
  return delay;
}

export function setReopenDeciding(now: number): void {
  try {
    localStorage.setItem(REOPEN_DECIDING_KEY, String(now));
  } catch (e) {
    // Storage unavailable: other tabs fall back to the 1.5 s rule.
  }
}

export function clearReopenDeciding(): void {
  try {
    localStorage.removeItem(REOPEN_DECIDING_KEY);
  } catch (e) {
    // Storage unavailable.
  }
}

/**
 * Until when another tab is presumed to be still asking (epoch ms), or null.
 * A value more than the stale window in the future (clock skew / junk) is
 * ignored, so it can never hold the report back for ever.
 */
export function reopenDecidingUntil(now: number): number | null {
  const at = readNumber(REOPEN_DECIDING_KEY);
  if (at === null || at - now > REOPEN_DECIDING_STALE_MS) {
    return null;
  }
  const until = at + REOPEN_DECIDING_STALE_MS;
  return until > now ? until : null;
}

export function markReopenReported(): void {
  try {
    if (isReopenLocked()) {
      localStorage.setItem(REOPEN_REPORTED_KEY, '1');
      clearReopenBackoff();
    }
  } catch (e) {
    // Storage unavailable: the next check reports again (harmless).
  }
}

export function isReopenReported(): boolean {
  try {
    return localStorage.getItem(REOPEN_REPORTED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

/** When the reopen lock was set; 0 for the old '1' / junk (long ago); null when unset. */
export function reopenLockedAt(): number | null {
  if (!isReopenLocked()) {
    return null;
  }
  let at = NaN;
  try {
    at = Number(localStorage.getItem(REOPEN_LOCKED_KEY));
  } catch (e) {
    return null;
  }
  return isFinite(at) && at > 1 ? at : 0;
}

/** Any stored value is a lock (the old '1' included). */
export function isReopenLocked(): boolean {
  try {
    const value = localStorage.getItem(REOPEN_LOCKED_KEY);
    return value !== null && value !== '';
  } catch (e) {
    return false;
  }
}

/**
 * Set less than REOPEN_LOCK_ANSWER_GRACE_MS ago. The old '1', junk and a
 * future value (clock skew) are not fresh, so they lock.
 */
export function isReopenLockFresh(now: number): boolean {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(REOPEN_LOCKED_KEY);
  } catch (e) {
    return false;
  }
  const at = raw ? Number(raw) : NaN;
  if (!isFinite(at)) {
    return false;
  }
  const age = now - at;
  return age >= 0 && age < REOPEN_LOCK_ANSWER_GRACE_MS;
}

/**
 * JS-2070: only the BroadcastChannel "every tab closed" flag locks. Refresh,
 * laptop lock, 5-minute inactivity, a new tab next to an open one and agency
 * switching never lock — the time-based idle check (isReopenExpired) is gone.
 * `ignoreFreshReopenLock`: only for a tab that is open and unlocked in the
 * app - a reopenLocked just set by a tab asking for answers does not lock it.
 */
export function shouldLockNow(now: number, ignoreFreshReopenLock: boolean = false): boolean {
  if (!hasDeviceSession()) {
    return false;
  }
  return isReopenLocked() && !(ignoreFreshReopenLock && isReopenLockFresh(now));
}

/**
 * JS-2070: the periodic heartbeat is gone. Signature kept so callers compile;
 * now always returns false. jwt.interceptor still stamps lastHeartbeat on
 * every api response (for the storage-event wake-up on the unlock screen); no
 * polling, no can-access-functionality keep-alive.
 */
export function shouldSendHeartbeat(_now: number): boolean {
  return false;
}

export function webSessionHeaders(): {[name: string]: string} {
  const token = localStorage.getItem(WEB_SESSION_KEY);
  return token ? {'X-Web-Session': token} : {};
}

const WEB_SESSION_ATTACHED = '__jsWebSessionAttached';

/**
 * For requests built outside HttpClient, which JwtInterceptor never sees:
 * set this device's session header on an opened, not yet sent XHR. Read at
 * send time - the token changes after an unlock. `now` is unused, kept so
 * existing call sites compile. Once per XHR: Dropzone's `sending` fires per file, and an
 * uploadMultiple batch shares one XHR (a second setRequestHeader appends).
 */
export function attachWebSessionHeaders(xhr: any, now: number = Date.now()): void {
  if (!xhr || typeof xhr.setRequestHeader !== 'function' || xhr[WEB_SESSION_ATTACHED]) {
    return;
  }
  xhr[WEB_SESSION_ATTACHED] = true;
  const headers = webSessionHeaders();
  Object.keys(headers).forEach(name => xhr.setRequestHeader(name, headers[name]));
}

/**
 * Hand-built Dropzone (ngx-dropzone-wrapper) uploads carry their own bearer
 * token in `headers`, which the wrapper copies once at init. Hook the
 * uploader's `sending` event instead so the device session is added per send.
 */
export function sendWebSessionOnUpload<T extends object>(config: T): T {
  (config as any).sending = (file: any, xhr: any) => attachWebSessionHeaders(xhr, Date.now());
  return config;
}

/** The returnUrl for the unlock screen, or null when we must not navigate. */
export function unlockNavigationFor(currentUrl: string): string | null {
  const url = currentUrl || '/';
  if (NO_LOCK_PREFIXES.some(prefix => url.startsWith(prefix))) {
    return null;
  }
  return url;
}

/** Only same-app relative paths; anything else (open redirect, loop) -> '/'. */
export function safeReturnUrl(raw: string | null): string {
  // Browsers strip tab/LF/CR from URLs, so '/\t/evil.com' reads as '//evil.com'.
  if (raw && /[\u0000-\u001f\u007f]/.test(raw)) {
    return '/';
  }
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
    return '/';
  }
  if (unlockNavigationFor(raw) === null) {
    return '/';
  }
  return raw;
}

/**
 * What ErrorInterceptor rethrows for an idle-lock 423. It is registered
 * twice per request (app + lazy modules), so the second pass and
 * SentryErrorHandler must recognise the first pass's rethrow and stay out
 * of Sentry; compared by identity, never by shape.
 */
export const IDLE_LOCK_ERROR: Readonly<{code: string}> = Object.freeze({code: 'reverify_required'});

/**
 * The api's idle-lock answer: 423 with code reverify_required. A download
 * (responseType 'blob') gets the JSON body as an unread Blob: any 423 with a
 * Blob body counts too - the api's only 423 is the idle lock.
 */
export function isReverifyRequiredResponse(err: any): boolean {
  if (err?.status !== 423) {
    return false;
  }
  if (typeof Blob !== 'undefined' && err.error instanceof Blob) {
    return true;
  }
  return err?.error?.code === 'reverify_required';
}

/** True for IDLE_LOCK_ERROR itself or an Angular { rejection: IDLE_LOCK_ERROR } wrapper. */
export function isIdleLockError(err: any): boolean {
  return err === IDLE_LOCK_ERROR || (err != null && err.rejection === IDLE_LOCK_ERROR);
}
